import React, { useState } from "react";
import "../../Styles/CmrFundsForm.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { cmrfAPI } from "../../api/index";

const MANDAL_NAMES = [
  "Kandukur",
  "Lingasamudram",
  "Gudluru",
  "Ulavapadu",
  "Voletivaripalem",
];

// Village name suggestions per mandal (autocomplete only — the backend is the
// real validator). Extend this with the real village list per mandal.
const VILLAGE_SUGGESTIONS = {
  Kandukur: ["Kandukur", "Kalavalla", "Kovur"],
  Lingasamudram: [],
  Gudluru: ["Gudluru"],
  Ulavapadu: [],
  Voletivaripalem: [],
};

// Matches the backend status values
const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

const initialForm = {
  name: "",
  relation: "",
  voterId: "",
  amount: "",
  village: "",
  mandal: "",
  date: "",
  status: "",
  remarks: "",
  videoUrl: "",
};

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyVideoUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

const AdminCmrFundsForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleReset = () => setFormData(initialForm);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedVillage = formData.village.trim();

    if (!formData.name.trim()) {
      toast.error("Beneficiary name is required");
      return;
    }
    if (!formData.mandal || formData.mandal === "Select Mandal") {
      toast.error("Please select a Mandal");
      return;
    }
    if (!trimmedVillage) {
      toast.error("Village is required");
      return;
    }
    if (!formData.status || formData.status === "Select Status") {
      toast.error("Please select a Status");
      return;
    }
    if (formData.videoUrl.trim() && !isLikelyVideoUrl(formData.videoUrl.trim())) {
      toast.error("Enter a valid video URL (e.g. a YouTube link starting with https://)");
      return;
    }

    const payload = {
      beneficiary_name: formData.name.trim(),
      relation_name: formData.relation.trim() || undefined,
      epic_no: formData.voterId.trim().toUpperCase() || undefined,
      amount: formData.amount !== "" ? Number(formData.amount) : undefined,
      mandal_name: formData.mandal,
      village_name: trimmedVillage,
      application_date: formData.date || undefined,
      status: formData.status,
      video_url: formData.videoUrl.trim() || undefined,
      remarks: formData.remarks.trim() || undefined,
    };

    setSubmitting(true);
    try {
      await cmrfAPI.create(payload);
      toast.success("CMRF beneficiary added successfully");
      handleReset();
      navigate(-1); // back to the CMRF table
    } catch (err) {
      console.error("CMRF create payload sent:", payload);
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const villageOptions = VILLAGE_SUGGESTIONS[formData.mandal] || [];

  return (
    <div className="beneficiary-form-container">
      <div className="form-card">
        <div className="form-header">
          <h2>Add New CMRF Beneficiary</h2>
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>Beneficiary Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Beneficiary Name"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Relation</label>
              <input
                type="text"
                name="relation"
                placeholder="Ex: S/O, W/O, D/O"
                value={formData.relation}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Voter Id</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter Voter Id"
                value={formData.voterId}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Amount (₹)</label>
              <input
                type="number"
                min="0"
                name="amount"
                placeholder="Enter Amount"
                value={formData.amount}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Mandal *</label>
              <select name="mandal" value={formData.mandal} onChange={handleChange}>
                <option>Select Mandal</option>
                {MANDAL_NAMES.map((m) => (
                  <option key={m}>{m}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village"
                value={formData.village}
                onChange={handleChange}
                list="village-suggestions"
                autoComplete="off"
              />
              <datalist id="village-suggestions">
                {villageOptions.map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
              <small
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  marginTop: "4px",
                  display: "block",
                }}
              >
                Must exactly match an existing village name under the selected Mandal.
                Select a Mandal first to see suggestions — double-check spelling either way.
              </small>
            </div>

            <div className="form-group">
              <label>Date</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Status *</label>
              <select name="status" value={formData.status} onChange={handleChange}>
                <option>Select Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Video URL (YouTube or direct link)</label>
              <input
                type="url"
                name="videoUrl"
                placeholder="https://www.youtube.com/watch?v=..."
                value={formData.videoUrl}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group full-width">
            <label>Remarks</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
            ></textarea>
          </div>

          <div className="btn-group">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>

            <button
              type="button"
              className="reset-btn"
              onClick={handleReset}
              disabled={submitting}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminCmrFundsForm;